import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IFilterTabsState, IPersistedFilterTab } from '../models/filter-tabs.models';
import { FilterTabsRepository } from './filter-tabs.repository';
import { GridLayoutV2Service } from './grid-layout-v2.service';
import { LocalStorageFilterTabsRepository } from './local-storage-filter-tabs.repository';
import { NotifyService } from './notify.service';

/** Reserved tab id that stores full filter-tabs state for a list key. */
export const FILTER_TABS_STATE_TAB_KEY = '_filterTabs';

/**
 * Persists list filter tabs via GridLayoutsV2.
 * Migrates existing localStorage tabs once when the backend has none yet.
 */
@Injectable({
    providedIn: 'root',
})
export class ApiFilterTabsRepository implements FilterTabsRepository {
    private readonly gridLayoutV2 = inject(GridLayoutV2Service);
    private readonly localStorageRepository = inject(LocalStorageFilterTabsRepository);
    private readonly notifyService = inject(NotifyService);
    private readonly translocoService = inject(TranslocoService);

    /** Unquoted ETag per list key (same pattern as GridStateStorageService). */
    private readonly versions = new Map<string, string | null>();

    load(listKey: string): Observable<IFilterTabsState | null> {
        return this.gridLayoutV2.getLayoutsByKey(listKey).pipe(
            switchMap(layouts => {
                const stateLayout = layouts.find(layout => layout.tabIdentifier === FILTER_TABS_STATE_TAB_KEY);

                if (stateLayout) {
                    this.versions.set(listKey, stateLayout.version || null);
                    return of(this.parseState(stateLayout.content));
                }

                return this.migrateFromLocalStorage(listKey);
            }),
            catchError(error => {
                console.error(`Failed to load filter tabs for "${listKey}" from backend:`, error);
                this.versions.delete(listKey);
                return of(null);
            })
        );
    }

    save(listKey: string, state: IFilterTabsState): Observable<void> {
        const content = JSON.stringify(this.normalizeState(state));
        const version = this.versions.get(listKey) ?? null;

        return this.gridLayoutV2.saveLayout(listKey, FILTER_TABS_STATE_TAB_KEY, content, version).pipe(
            tap(saved => {
                this.versions.set(listKey, saved.version || null);
                this.localStorageRepository.clear(listKey);
            }),
            map(() => undefined)
        );
    }

    private migrateFromLocalStorage(listKey: string): Observable<IFilterTabsState | null> {
        return this.localStorageRepository.load(listKey).pipe(
            switchMap(localState => {
                if (!localState || (!localState.tabs.length && !localState.selectedTabKey)) {
                    this.versions.set(listKey, null);
                    return of(null);
                }

                const normalized = this.normalizeState(localState);

                return this.gridLayoutV2.saveLayout(listKey, FILTER_TABS_STATE_TAB_KEY, JSON.stringify(normalized), null).pipe(
                    tap(saved => {
                        this.versions.set(listKey, saved.version || null);
                        this.localStorageRepository.clear(listKey);
                    }),
                    map(() => normalized),
                    catchError(error => {
                        console.error(`Failed to migrate filter tabs for "${listKey}" to backend:`, error);
                        this.versions.set(listKey, null);
                        this.notifyService.notifyErrorText(this.translocoService.translate('message_filter_tabs_save_failed'));
                        return of(normalized);
                    })
                );
            })
        );
    }

    private parseState(content: string): IFilterTabsState | null {
        try {
            const parsed = JSON.parse(content) as IFilterTabsState;
            if (!parsed || !Array.isArray(parsed.tabs)) {
                return null;
            }
            return this.normalizeState(parsed);
        } catch {
            console.error('Failed to parse filter tabs state from backend content');
            return null;
        }
    }

    private normalizeState(state: IFilterTabsState): IFilterTabsState {
        return {
            tabs: (state.tabs ?? []).map(tab => this.normalizeTab(tab)),
            selectedTabKey: state.selectedTabKey ?? null,
        };
    }

    private normalizeTab(tab: IPersistedFilterTab): IPersistedFilterTab {
        return {
            key: tab.key,
            text: tab.text,
            filterValue: tab.filterValue ?? null,
            dataSourceFilter: tab.dataSourceFilter ?? null,
            columns: tab.columns ?? null,
        };
    }
}
